import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingEntry } from '../../models/accounting.entry.model';
import { Company } from '../../models/company.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Invoice } from '../../models/invoice.model';
import { ProviderInvoice } from '../../models/provider.invoice.model';
import { legendValid, legendIconValid, legendIconValidInvalidRestart, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { formatAccountingEntry } from '../../scripts/format.value.script';
import { prefixLegend } from '../../scripts/name.legend.script';

import AppLegend from './Legend';

export interface AppInputAccountingEntryProps {
  component?: string,
  options: {solid: boolean, transaction: string},
  companyForUser: Company,
  accountingEntriesForCompany: AccountingEntry[],
  treasuryAccountsForCompany: TreasuryAccount[],
  contact?: string,
  record?: Invoice | ProviderInvoice,
  accountingEntry: {value: string, valid: boolean, object: any},
  setAccountingEntry: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setAmount?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAmountPay?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAmountDeposit?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTreasuryAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setPaymentMethod: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean, starting: boolean}>>
};

const AppInputAccountingEntry: React.FunctionComponent<AppInputAccountingEntryProps> = ({component, options, companyForUser, accountingEntriesForCompany, treasuryAccountsForCompany, contact, record, accountingEntry, setAccountingEntry, setAmountPay, setAmount, setAmountDeposit, setTreasuryAccount, setPaymentMethod, setDate}) => {
  const {lang} = StateLanguage()

  const handleChangeAccountingEntry = (item: AccountingEntry) => {
    setAccountingEntry({value: item.id, valid: true, object: item})
    setTreasuryAccount({value: '', valid: false, object: null})
    setPaymentMethod({value: '', valid: false})
    setDate({value: '', valid: false, starting: true})

    legendValid(`${prefixLegend(component)}legend-accounting-entry-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-treasury-account-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-payment-method-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-date-required`)

    if (setAmount) {
      setAmount({value: '', valid: false})

      legendIconValidInvalidRestart(`${prefixLegend(component)}input-amount`, `${prefixLegend(component)}legend-amount-valid`)
      legendIconValidInvalidRestart(`${prefixLegend(component)}input-amount`, `${prefixLegend(component)}legend-amount-required`)
    }

    if (record && setAmountPay && setAmountDeposit) {
      setAmountPay({value: (item.balance >= record.amount.debt) ? record.amount.debt.toString() : item.balance.toString(), valid: true})
      setAmountDeposit({value: '', valid: false})

      legendIconValid(`${prefixLegend(component)}input-amount-pay`, `${prefixLegend(component)}legend-amount-pay-valid`)
      legendIconValid(`${prefixLegend(component)}input-amount-pay`, `${prefixLegend(component)}legend-amount-pay-required`)
      legendIconValidInvalidRestart(`${prefixLegend(component)}input-amount-deposit`, `${prefixLegend(component)}legend-amount-deposit-valid`)
      legendIconValidInvalidRestart(`${prefixLegend(component)}input-amount-deposit`, `${prefixLegend(component)}legend-amount-deposit-required`)
    }
  }

  function accountingIdentifiedEntries(): AccountingEntry[] {
    return accountingEntriesForCompany.filter((item) => (item.id_contact.length > 0 && item.id_contact === contact));
  }

  function accountingUnidentifiedEntries(): AccountingEntry[] {
    return accountingEntriesForCompany.filter((item) => (item.id_contact.length === 0));
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <span className="select2-selection select2-selection--single form-select">
              <span className={options.solid ? "select2-selection__rendered" : ""} role="textbox">
                {accountingEntry.valid ? formatAccountingEntry(lang, companyForUser.currency, accountingEntry.object, treasuryAccountsForCompany, options.transaction) : lang.labels.selectOption}
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open" renderOnMount popperConfig={{ strategy: 'fixed', modifiers: [{ name: 'sameWidth', enabled: true, phase: 'beforeWrite' as const, requires: ['computeStyles'], fn: ({ state }: any) => { state.styles.popper.width = `${state.rects.reference.width}px` }, effect: ({ state }: any) => { state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px` } }] }}>
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox" style={{ maxHeight: 320 }}>
                <li className={`select2-results__option select2-results__option--selectable ${accountingEntriesForCompany.length > 0 && "d-none"}`}>
                  <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                </li>
                { record && accountingIdentifiedEntries().length > 0 &&
                  <>
                    <span className={`select2-results__option select2-results__option--group ${accountingUnidentifiedEntries().length === 0 && "d-none"}`}>
                      <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.identifiedTransactions}</Dropdown.Header>
                    </span>
                    <ul className="select2-results__options mh-200px mb-2" role="listbox">
                      { accountingIdentifiedEntries().map (( (item, index) => { return (
                        <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accountingEntry.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accountingEntry.value}>
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(item)}>{formatAccountingEntry(lang, companyForUser.currency, item, treasuryAccountsForCompany, options.transaction)}</Dropdown.Item>
                        </li>
                      )}))}
                    </ul>
                  </>
                }
                { accountingUnidentifiedEntries().length > 0 &&
                  <>
                    { record &&
                      <span className={`select2-results__option select2-results__option--group ${accountingIdentifiedEntries().length === 0 && "d-none"}`}>
                        <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.unidentifiedTransactions}</Dropdown.Header>
                      </span>
                    }
                    <ul className="select2-results__options mh-200px" role="listbox">
                      { accountingUnidentifiedEntries().map (( (item, index) => { return (
                        <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accountingEntry.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accountingEntry.value}>
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(item)}>{formatAccountingEntry(lang, companyForUser.currency, item, treasuryAccountsForCompany, options.transaction)}</Dropdown.Item>
                        </li>
                      )}))}
                    </ul>
                  </>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { !record && !setAmountPay && !setAmountDeposit &&
        <div className="form-text text-justify">{lang.labels.unidentifiedTransactionToRegisterInContact}</div>
      }
      <AppLegend component={component} attribute={{check: accountingEntry.valid, name: "accounting-entry", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
    </>
  )
}

export default AppInputAccountingEntry;
